// Cari, the destination deck.
//
// THREE RULES THIS SCREEN IS BUILT AROUND, none of them optional:
//
// 1. The gesture is an ENHANCEMENT. A swipe is invisible, undiscoverable and
//    unreachable by keyboard or a screen reader, so the two plain buttons
//    below the card are the real interface and the drag is layered on top of
//    exactly the same two functions.
// 2. Empty and exhausted are real states with real words, never a blank card.
//
// There is deliberately NO undo control and NO remaining count. Brian removed
// both: the row is two decisions, and a third control plus a running tally
// turned a one thumb screen into something to read. A dismissed destination is
// still reachable through search, so a mis-swipe is recoverable, just not from
// here. `DELETE /discover/swipe/:placeId` still exists on the API for that.
//
// Motion is transform and opacity only, and `prefers-reduced-motion` drops
// the fling entirely and applies the result immediately.
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import {
  Icon,
  IconBookmark,
  IconClose,
  IconHeart,
  IconRefresh,
} from "@traveldiary/ui";
import { EmptyState } from "../components/EmptyState";
import { apiClient } from "../lib/api";
import { formatMoney, formatNumber } from "../lib/format";
import {
  clearSwipeLocal,
  filterDeck,
  readSwipes,
  recordSwipeLocal,
  type SwipeLog,
} from "../lib/discover";
import { groupMomentsIntoPlaces, momentsAtPlace } from "../lib/places";
import { navigate } from "../lib/router";
import type { DiscoverCard, SwipeDirection, WishlistItem } from "../lib/types";
import { useApp } from "../state/app";

/** Past this fraction of the card's width, letting go commits the swipe. */
const COMMIT_RATIO = 0.28;
const COMMIT_FLOOR_PX = 72;
const FLING_MS = 220;

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

export function DiscoverScreen() {
  const { t, locale, exploreMoments, wishlist, saveToWishlist } = useApp();

  const [queue, setQueue] = useState<DiscoverCard[]>([]);
  const [log, setLog] = useState<SwipeLog>(() => readSwipes());
  const [status, setStatus] = useState<string>("");
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const [fling, setFling] = useState<SwipeDirection | null>(null);

  const statusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const startRef = useRef<{ x: number; y: number; id: number } | null>(null);
  const flingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Fallback deck. When this API build has no `/discover` yet the deck is
   * grouped from the public feed with the same place rule the map uses, so
   * Cari is never a dead end. Budget is left null on purpose: there is no
   * pricing source on the client and inventing one would be worse than
   * showing none.
   */
  const localDeck = useMemo<DiscoverCard[]>(() => {
    const places = groupMomentsIntoPlaces(exploreMoments);
    return places
      .map((place) => {
        const entries = momentsAtPlace(exploreMoments, place.id);
        const media = entries.flatMap((entry) => entry.media).slice(0, 1);
        const summary = entries.find((entry) => entry.caption.trim())?.caption ?? null;
        return {
          placeId: place.id,
          name: place.placeName || t("explore.noPlace"),
          lat: place.lat,
          lng: place.lng,
          region: null,
          media,
          travellerCount: place.travellerCount,
          budget: null,
          summary,
        } satisfies DiscoverCard;
      })
      .filter((card) => card.media.length > 0);
  }, [exploreMoments, t]);

  const load = useCallback(async () => {
    const current = readSwipes();
    setLog(current);
    try {
      const cards = await apiClient.listDiscover();
      setQueue(filterDeck(cards ?? localDeck, current));
      setFailed(false);
    } catch {
      // A dead network is only a failure if there is nothing cached to show.
      // With a cached public feed the deck still works, so it stays.
      const fallback = filterDeck(localDeck, current);
      setQueue(fallback);
      setFailed(fallback.length === 0);
    } finally {
      setLoaded(true);
    }
  }, [localDeck]);

  useEffect(() => {
    void load();
    // Reloading on every localDeck identity change would reshuffle the deck
    // under the traveller's thumb, so this runs once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A card the traveller has not seen yet should not flash white when it
  // reaches the front, so the image after next is fetched ahead of time.
  useEffect(() => {
    const ahead = queue[2]?.media[0];
    if (!ahead || ahead.mime.startsWith("video/")) return;
    const image = new Image();
    image.src = apiClient.mediaUrl(ahead.storageKey);
  }, [queue]);

  useEffect(
    () => () => {
      if (flingTimer.current) clearTimeout(flingTimer.current);
      if (statusTimer.current) clearTimeout(statusTimer.current);
    },
    [],
  );

  // A confirmation, not a label: it hands the row back to the hint so the
  // next card is explained the same way the first one was.
  useEffect(() => {
    if (!status) return;
    statusTimer.current = setTimeout(() => setStatus(""), 2600);
    return () => {
      if (statusTimer.current) clearTimeout(statusTimer.current);
    };
  }, [status]);

  const front = queue[0] ?? null;
  const behind = queue[1] ?? null;

  const apply = useCallback(
    (card: DiscoverCard, direction: SwipeDirection) => {
      setLog((current) => recordSwipeLocal(current, card.placeId, direction));
      setQueue((current) => current.filter((item) => item.placeId !== card.placeId));
      setDrag(null);
      setFling(null);

      if (direction === "right") {
        const item: WishlistItem = {
          placeId: card.placeId,
          name: card.name,
          lat: card.lat,
          lng: card.lng,
          media: card.media,
          budget: card.budget,
          addedAt: new Date().toISOString(),
        };
        void saveToWishlist(item);
        setStatus(t("discover.saved"));
      } else {
        setStatus(t("discover.skipped"));
      }

      void apiClient.recordSwipe(card.placeId, direction).catch(() => {
        // The local log already remembers it, so the deck behaves. The next
        // load reconciles with whatever the server holds.
      });
    },
    [saveToWishlist, t],
  );

  const commit = useCallback(
    (direction: SwipeDirection) => {
      const card = front;
      if (!card || fling) return;
      if (prefersReducedMotion()) {
        apply(card, direction);
        return;
      }
      setFling(direction);
      flingTimer.current = setTimeout(() => apply(card, direction), FLING_MS);
    },
    [apply, fling, front],
  );


  /* ------------------------------------------------------------- gesture */

  function onPointerDown(event: PointerEvent<HTMLDivElement>): void {
    if (!front || fling) return;
    // A drag that starts on a control is the control's, not the deck's.
    if ((event.target as HTMLElement).closest("button, a")) return;
    startRef.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({ dx: 0, dy: 0 });
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>): void {
    const start = startRef.current;
    if (!start || start.id !== event.pointerId) return;
    setDrag({ dx: event.clientX - start.x, dy: event.clientY - start.y });
  }

  function endDrag(event: PointerEvent<HTMLDivElement>): void {
    const start = startRef.current;
    if (!start || start.id !== event.pointerId) return;
    startRef.current = null;
    const dx = event.clientX - start.x;
    const width = stageRef.current?.clientWidth ?? 320;
    const threshold = Math.max(COMMIT_FLOOR_PX, width * COMMIT_RATIO);
    setDrag(null);
    if (Math.abs(dx) >= threshold) commit(dx > 0 ? "right" : "left");
  }

  const width = stageRef.current?.clientWidth ?? 320;
  const progress = drag ? Math.max(-1, Math.min(1, drag.dx / Math.max(1, width * COMMIT_RATIO))) : 0;

  const frontStyle = fling
    ? {
        transform: `translate3d(${fling === "right" ? 130 : -130}%, 0, 0) rotate(${fling === "right" ? 12 : -12}deg)`,
        opacity: 0,
        transition: `transform ${FLING_MS}ms var(--ease-out), opacity ${FLING_MS}ms var(--ease-out)`,
      }
    : drag
      ? {
          transform: `translate3d(${drag.dx}px, ${drag.dy * 0.2}px, 0) rotate(${progress * 8}deg)`,
          transition: "none",
        }
      : undefined;

  /* -------------------------------------------------------------- render */

  const remaining = queue.length;
  const exhausted = loaded && remaining === 0;
  const neverHadAny = exhausted && Object.keys(log).length === 0;

  return (
    <div className="deck-screen">
      <div className="deck-stage" ref={stageRef}>
        {failed ? (
          <div className="deck-empty">
            <EmptyState
              glyph={<IconRefresh />}
              title={t("discover.failed")}
              hint={t("discover.noneHint")}
              action={
                <button type="button" className="btn btn-quiet" onClick={() => void load()}>
                  <Icon size={18}>
                    <IconRefresh />
                  </Icon>
                  {t("common.retry")}
                </button>
              }
            />
          </div>
        ) : neverHadAny ? (
          <div className="deck-empty">
            <EmptyState
              glyph={<IconBookmark />}
              title={t("discover.none")}
              hint={t("discover.noneHint")}
            />
          </div>
        ) : exhausted ? (
          <div className="deck-empty">
            <EmptyState
              glyph={<IconBookmark />}
              title={t("discover.empty")}
              hint={t("discover.emptyHint")}
              action={
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn btn-quiet"
                    onClick={() => navigate("wishlist")}
                  >
                    <Icon size={18}>
                      <IconBookmark />
                    </Icon>
                    {t("discover.openWishlist")}
                  </button>
                </div>
              }
            />
          </div>
        ) : (
          <>
            {behind ? <DeckCard card={behind} behind /> : null}
            {front ? (
              <div
                className="deck-drag"
                style={frontStyle}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                <DeckCard card={front} progress={progress} />
              </div>
            ) : null}
          </>
        )}
      </div>

      <div className="deck-bar">
        {/*
          One line, always. The status REPLACES the hint rather than sitting
          next to it: two sentences competing for the same row wrapped to two
          ragged lines and pushed the controls around after every swipe.
        */}
        <p className="deck-note">
          <span className="deck-hint" aria-live="polite">
            {status || t("discover.hint")}
          </span>
        </p>
        <div className="deck-controls">
          <button
            type="button"
            className="btn btn-quiet deck-action"
            onClick={() => commit("left")}
            disabled={!front}
            aria-label={t("discover.dismiss")}
          >
            <Icon size={20}>
              <IconClose />
            </Icon>
            {t("discover.dismissShort")}
          </button>
          <button
            type="button"
            className="btn btn-primary deck-action"
            onClick={() => commit("right")}
            disabled={!front}
            aria-label={t("discover.accept")}
          >
            <Icon size={20} color="var(--color-primary-contrast)">
              <IconHeart />
            </Icon>
            {t("discover.acceptShort")}
          </button>
        </div>
        <p className="deck-legal">
          {t("discover.budgetDummy")}
          {wishlist.length > 0 ? ` ${formatNumber(wishlist.length, locale)} ${t("wishlist.selected")}.` : ""}
        </p>
      </div>
    </div>
  );
}

/**
 * One destination. Content order is fixed by the contract: media first, then
 * the name, then the budget estimate when there is one. Every card is the
 * same shape by construction, the media absorbs the height difference so a
 * missing budget or a long name can never reshape the deck.
 */
function DeckCard({
  card,
  behind,
  progress = 0,
}: {
  card: DiscoverCard;
  behind?: boolean;
  progress?: number;
}) {
  const { t, locale } = useApp();
  const media = card.media[0];
  const isVideo = media?.mime.startsWith("video/") ?? false;

  return (
    <article className={behind ? "deck-card deck-card-behind" : "deck-card"} aria-hidden={behind}>
      <div className="deck-media">
        {media ? (
          isVideo ? (
            <video
              className="deck-media-item"
              src={apiClient.mediaUrl(media.storageKey)}
              muted
              loop
              playsInline
              autoPlay
              preload="metadata"
            />
          ) : (
            <img
              className="deck-media-item"
              src={apiClient.mediaUrl(media.storageKey)}
              alt=""
              draggable={false}
            />
          )
        ) : null}
        {!behind && progress !== 0 ? (
          <span
            className={progress > 0 ? "deck-stamp deck-stamp-yes" : "deck-stamp deck-stamp-no"}
            style={{ opacity: Math.min(1, Math.abs(progress)) }}
          >
            {progress > 0 ? t("discover.acceptShort") : t("discover.dismissShort")}
          </span>
        ) : null}
      </div>

      <div className="deck-body">
        <h2 className="deck-name">{card.name}</h2>
        <p className="deck-meta">
          {card.region ? `${card.region}. ` : ""}
          {formatNumber(card.travellerCount, locale)} {t("discover.travellers")}
        </p>
        <div className="deck-budget">
          <span className="deck-budget-label">{t("discover.budget")}</span>
          <span className="deck-budget-value">
            {card.budget
              ? `${formatMoney(card.budget.currency, card.budget.min, locale)} ${t("trips.until")} ${formatMoney(card.budget.currency, card.budget.max, locale)} ${t("discover.perPerson")}`
              : t("budget.noEstimate")}
          </span>
          {/* What the range actually covers. A number with no scope is the
              kind of figure a traveller mistakes for a real price. */}
          {card.budget?.note ? (
            <span className="deck-budget-note">{card.budget.note}</span>
          ) : null}
        </div>
      </div>
    </article>
  );
}
